<script setup lang="ts">
import { joinURL, withQuery } from 'ufo';
import type { DropdownMenuItem } from '#ui/types';
import { getLocaleDescription, type I18nLocale } from '~~/models/Locale';
import { useCreatorQuery } from '~/queries/useCreatorQuery';
import { useMerchantsQuery } from '~/queries/useMerchantsQuery';
import { useSupabaseUser } from '~/composables/useSupabaseUser';
import { useCreatorAvatar } from '~/composables/useCreatorAvatar';
import { useMerchantPublisherId } from '~/composables/useMerchantPublisherId';
import { useDefaultProgramme } from '~/composables/useDefaultProgramme';
import { useArray } from '~/composables/useArray';
import { useMerchantSlug } from '~/composables/useMerchantSlug';
import { useGlobalMerchant } from '~/composables/useGlobalMerchant';
import { useMerchantUrlUtils } from '~/composables/useMerchantUrlUtils';
import { insertIf } from '~/utils';
import { getProgrammeApplicationStatus } from '~~/models/Programme';

defineProps<{
	collapsed?: boolean;
}>();

const { t } = useI18n();

const { data: merchantsData } = useMerchantsQuery();
const merchants = useArray(merchantsData);

const route = useRoute();

const merchantSlug = useMerchantSlug(route);

const router = useRouter();

const merchant = useGlobalMerchant();

const { isOnCorrectMerchantUrl, hasMerchantStaticUrl, getRouteWithStaticSlug, getMerchantUrl } = useMerchantUrlUtils();

const merchantNavigation = computed<DropdownMenuItem[][]>(() => {
	return [merchants.value.map<DropdownMenuItem>((_merchant) => {
		const needsRedirect = !isOnCorrectMerchantUrl(_merchant.slug, window.location.origin);
		const merchantUrl = getMerchantUrl(_merchant.slug);
		const newRoute = router.resolve({ name: 'MerchantDashboard', params: { merchantSlug: _merchant.slug } });
		const newRouteWithCorrectSlug = hasMerchantStaticUrl(_merchant.slug) ? getRouteWithStaticSlug(newRoute) : newRoute;

		return {
			label: _merchant.name,
			to: needsRedirect ? withQuery(joinURL(merchantUrl, newRouteWithCorrectSlug.href), { redirectFromAnotherSpace: true }) : newRouteWithCorrectSlug,
			external: needsRedirect,
		};
	})];
});

const {
	locale: currentLocaleCode, setLocale, locales,
} = useI18n();

const switchLanguage = (code: I18nLocale) => {
	setLocale(code);
};

const languageNavigation = computed<DropdownMenuItem[][]>(() => {
	return [locales.value.map<DropdownMenuItem>((_locale) => ({
		label: getLocaleDescription(_locale.code),
		onSelect: () => switchLanguage(_locale.code),
	}))];
});

const colorMode = useColorMode();

const themeNavigation = computed<DropdownMenuItem[]>(() => {
	return [
		{
			label: 'Light',
			onSelect: () => colorMode.preference = 'light',
		},
		{
			label: 'Dark',
			onSelect: () => colorMode.preference = 'dark',
		},
	];
});

const userNavigation = computed<DropdownMenuItem[][]>(() => [
	[{
		slot: 'account',
		type: 'label',
	}],
	[
		...insertIf<DropdownMenuItem>(merchantSlug.value, {
			label: t('topbar.userMenu.links.account'),
			icon: 'i-lucide-settings',
			to: { name: 'AccountOverview', params: { merchantSlug: merchantSlug.value! } },
		}),
		{
			label: t('topbar.userMenu.links.logout'),
			icon: 'i-lucide-log-out',
			to: { name: merchant ? 'MerchantLogout' : 'GlobalLogout' },
		},
	],
	[...insertIf<DropdownMenuItem>(merchants.value.length > 1, {
		slot: 'merchant',
		label: `Space: ${merchant.value?.name}`,
		children: merchantNavigation.value,
		content: { align: 'end' },
	})],
	[
		{
			slot: 'language',
			label: getLocaleDescription(currentLocaleCode.value),
			children: languageNavigation.value,
			icon: 'i-lucide-globe',
			content: { align: 'end' },
		},
		{
			slot: 'theme',
			label: 'Theme',
			children: themeNavigation.value,
			icon: 'i-lucide-swatch-book',
			content: { align: 'end' },
			badge: 'Coming soon',
		},
	],
]
	.filter((item) => !!item.length) as DropdownMenuItem[][]);

const user = useSupabaseUser();

const { data: creatorData } = useCreatorQuery();

const avatarUrlFromUserMetaData = computed(() => user.value?.user_metadata?.avatar_url);

const avatarUrl = useCreatorAvatar({ creator: creatorData, fallbackUrl: avatarUrlFromUserMetaData });

const { publisherId } = useMerchantPublisherId({ merchantId: computed(() => merchant.value?.id) });

const { programme } = useDefaultProgramme();

const showPublisherId = computed(() => {
	if (!programme.value) {
		return false;
	}

	const status = getProgrammeApplicationStatus(programme.value);

	return status === 'Approved';
});
</script>

<template>
	<UDropdownMenu
		:items="userNavigation"
		:content="{ align: 'center', collisionPadding: 12 }"
		:ui="{ content: 'w-(--reka-dropdown-menu-trigger-width) min-w-48' }"
	>
		<template #default>
			<UButton
				color="neutral"
				variant="ghost"
				:class="['w-full data-[state=open]:bg-elevated overflow-hidden', { 'justify-center': collapsed }]"
				square
				:label="collapsed ? undefined : creatorData?.name"
				:avatar="{
					src: avatarUrl,
					alt: creatorData?.name,
				}"
				:trailing-icon="collapsed ? undefined : 'i-lucide-ellipsis-vertical'"
				:ui="{
					trailingIcon: 'text-dimmed ms-auto'
				}"
			/>
		</template>

		<template #account>
			<div class="text-left w-full text-muted text-sm">
				<p class="font-light">
					{{ $t('sidebar.signedInAs') }}
				</p>
				<p class="truncate font-medium">
					{{ user?.email }}
				</p>
				<p
					v-if="showPublisherId && publisherId"
					class="font-light"
				>
					{{ $t('label.yourPublisherId') }}: <span class="font-medium">{{ publisherId }}</span>
				</p>
			</div>
		</template>

		<template #merchant-leading>
			<UAvatar
				:alt="merchant?.name"
				size="2xs"
				class="bg-primary-500 "
				:ui="{ fallback: 'text-white' }"
			/>
		</template>

		<template #theme-trailing>
			<UBadge
				size="sm"
				variant="subtle"
			>
				<!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
				<!---->
				Beta
			</UBadge>
		</template>
	</UDropdownMenu>
</template>
